import { useState } from 'react'
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import type { LoanEntryType } from '@/api/loans'
import { useFormatCurrency, useListAmountFormat } from '@/hooks/useFormatCurrency'
import { formatDate, formatShortDate } from '@/lib/formatDate'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { LoanEntryWithOutstanding, LoanYearGroup } from './loanStats'

const ENTRY_LABELS: Record<LoanEntryType, string> = {
  EMI: 'EMI',
  PREPAYMENT: 'Prepayment',
  INCREASE: 'Outstanding increased',
}

const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 sm:grid-cols-[72px_minmax(0,1.4fr)_repeat(3,minmax(84px,0.7fr))_32px]'

function EntryRow({
  e,
  accountName,
  onDelete,
  deleting,
}: {
  e: LoanEntryWithOutstanding
  accountName: (id: string | null) => string | undefined
  onDelete?: () => void
  deleting: boolean
}) {
  const { formatCompact } = useFormatCurrency()
  const format = useListAmountFormat()
  const account = accountName(e.accountId)
  const increase = e.type === 'INCREASE'
  const accountLine = increase
    ? account
      ? `Received into ${account}`
      : 'No account moved'
    : account
      ? `From ${account}`
      : null

  return (
    <li className={cn(ROW_GRID, 'border-b border-[var(--fl-divider)] px-4 py-2.5 last:border-b-0')}>
      <span className="hidden text-[13px] tabular-nums text-[var(--fl-text-2)] sm:block">
        {formatShortDate(e.date)}
      </span>
      <span className="min-w-0">
        <span className="text-[13.5px] font-medium text-[var(--fl-text-1)]">
          {ENTRY_LABELS[e.type]}
        </span>
        {e.type === 'PREPAYMENT' && (
          <span className="ml-1.5 rounded-full bg-[var(--fl-primary-faint)] px-1.5 py-px text-[11px] font-semibold text-[var(--fl-primary)]">
            extra
          </span>
        )}
        <span className="block truncate text-xs text-[var(--fl-text-3)]">
          <span className="sm:hidden">{formatShortDate(e.date)}</span>
          {accountLine && <span className="sm:hidden"> · </span>}
          {accountLine}
        </span>
      </span>
      {/* Phone: one combined figure with the split underneath. */}
      <span className="text-right sm:hidden">
        <span className="block text-[13.5px] font-semibold tabular-nums text-[var(--fl-heading)]">
          {increase
            ? `+${format(e.principalAmount)}`
            : format(e.principalAmount + e.interestAmount)}
        </span>
        <span className="block text-[11.5px] text-[var(--fl-text-3)]">
          {e.type === 'EMI'
            ? `${formatCompact(e.principalAmount)} P · ${formatCompact(e.interestAmount)} I`
            : `after ${formatCompact(e.outstandingAfter)}`}
        </span>
      </span>
      <span
        className={cn(
          'hidden text-right text-[13.5px] tabular-nums sm:block',
          increase ? 'text-[var(--fl-expense)]' : 'text-[var(--fl-income)]'
        )}
      >
        {increase ? `+${format(e.principalAmount)}` : format(e.principalAmount)}
      </span>
      <span className="hidden text-right text-[13.5px] tabular-nums text-[var(--fl-warning-text)] sm:block">
        {e.interestAmount > 0 ? format(e.interestAmount) : '—'}
      </span>
      <span className="hidden text-right text-[13.5px] font-semibold tabular-nums text-[var(--fl-heading)] sm:block">
        {format(e.outstandingAfter)}
      </span>
      <span className="flex justify-end">
        {onDelete && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onDelete}
            disabled={deleting}
            className="text-[var(--fl-text-3)] hover:text-[var(--fl-error)]"
            tooltip="Delete"
            aria-label={`Delete ${ENTRY_LABELS[e.type]} of ${formatDate(e.date)}`}
          >
            <Trash2 size={14} />
          </Button>
        )}
      </span>
    </li>
  )
}

export function LoanHistory({
  groups,
  accountName,
  onDelete,
  deleting,
}: {
  groups: LoanYearGroup[]
  accountName: (id: string | null) => string | undefined
  /** Omitted when the loan can't be edited, which hides Delete. */
  onDelete?: (entryId: string) => void
  deleting: boolean
}) {
  const { format } = useFormatCurrency()
  // Only the latest year starts open, so a long-running loan doesn't become a wall of rows.
  const [open, setOpen] = useState<Set<number>>(
    () => new Set(groups.slice(0, 1).map((g) => g.year))
  )

  function toggle(year: number) {
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(year)) next.delete(year)
      else next.add(year)
      return next
    })
  }

  return (
    <div className="overflow-hidden rounded-[14px] border border-[var(--fl-border)] bg-[var(--fl-surface)]">
      <div
        aria-hidden="true"
        className={cn(
          ROW_GRID,
          'hidden border-b border-[var(--fl-divider)] px-4 py-2 text-[11.5px] font-semibold text-[var(--fl-text-3)] sm:grid'
        )}
      >
        <span>Date</span>
        <span>Entry</span>
        <span className="text-right">Principal</span>
        <span className="text-right">Interest</span>
        <span className="text-right">Outstanding after</span>
        <span />
      </div>
      {groups.map((g) => {
        const isOpen = open.has(g.year)
        return (
          <section key={g.year} aria-label={g.label}>
            <button
              type="button"
              onClick={() => toggle(g.year)}
              aria-expanded={isOpen}
              className="flex w-full items-baseline justify-between gap-2 border-b border-[var(--fl-divider)] bg-[var(--fl-surface-subtle)] px-4 py-2.5 text-left text-[13px] hover:bg-[var(--fl-surface-hover)]"
            >
              <span className="flex items-center gap-1 text-sm font-semibold text-[var(--fl-heading)]">
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {g.label}
              </span>
              <span className="tabular-nums text-[var(--fl-text-2)]">
                {g.entries.length} {g.entries.length === 1 ? 'entry' : 'entries'}
                <span className="hidden sm:inline">
                  {' '}
                  · principal {format(g.principal)} · interest {format(g.interest)}
                </span>
              </span>
            </button>
            {isOpen && (
              <ul>
                {g.entries.map((e) => (
                  <EntryRow
                    key={e.id}
                    e={e}
                    accountName={accountName}
                    onDelete={onDelete ? () => onDelete(e.id) : undefined}
                    deleting={deleting}
                  />
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}
